import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import UserMenu from '../components/UserMenu';
import ErrorBanner from '../components/ErrorBanner';
import Modal from '../components/Modal';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import { AnalisisItem, buscarAnalisis, descargarPdf, eliminarAnalisis, verPdf } from '../api/analisisApi';
import { nombreArchivoResultado } from '../utils/pdfFilename';
import { ApiError } from '../types/auth.types';
import { useAuth } from '../context/AuthContext';
import styles from './BuscarAnalisisPage.module.css';

function formatFecha(fecha: string): string {
  const [year, month, day] = fecha.split('-');
  return `${day}/${month}/${year}`;
}

export default function BuscarAnalisisPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esMedico = user?.role === 'MEDICO';

  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState<AnalisisItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmItem, setConfirmItem] = useState<AnalisisItem | null>(null);

  async function handleSearch(e: FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const data = await buscarAnalisis(query.trim());
      setResultados(data);
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'Error al buscar análisis.');
      } else {
        setError('Error al buscar análisis.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function confirmarEliminar() {
    if (!confirmItem) return;
    const id = confirmItem.id;
    setConfirmItem(null);
    setDeletingId(id);
    try {
      await eliminarAnalisis(id);
      setResultados((prev) => prev?.filter((r) => r.id !== id) ?? null);
    } catch {
      setError('No se pudo eliminar el análisis.');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleDescargar(item: AnalisisItem) {
    setDownloadingId(item.id);
    try {
      const nombre = nombreArchivoResultado(item.pacienteApellido, item.pacienteNombre, item.fecha);
      await descargarPdf(item.id, nombre);
    } catch {
      setError('No se pudo descargar el PDF.');
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleVer(item: AnalisisItem) {
    setViewingId(item.id);
    try {
      await verPdf(item.id);
    } catch {
      setError('No se pudo abrir el PDF.');
    } finally {
      setViewingId(null);
    }
  }

  return (
    <>
    <div className={styles.page}>
      <header className={styles.topbar}>
        {!esMedico && (
          <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>← Volver</button>
        )}
        <img src={logo} alt="Logo" className={styles.topbarLogo} />
        <p className={styles.topbarTitle}>Buscar análisis</p>
        <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        <form className={styles.searchRow} onSubmit={handleSearch}>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Buscar por nombre, apellido o DNI del paciente..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className={styles.searchBtn} type="submit" disabled={loading || !query.trim()}>
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </form>

        {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

        {loading && (
          <div className={styles.spinnerWrapper}>
            <div className={styles.spinner} />
            <p className={styles.spinnerText}>Buscando análisis...</p>
          </div>
        )}

        {!loading && resultados !== null && (
          resultados.length === 0 ? (
            <div className={styles.emptyState}>
              No se encontraron análisis para la búsqueda realizada.
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Fecha</th>
                    <th>Estudios realizados</th>
                    <th>PDF</th>
                    {!esMedico && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {resultados.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className={styles.pacienteName}>
                          {item.pacienteApellido}, {item.pacienteNombre}
                        </div>
                        <div className={styles.pacienteDni}>DNI {item.pacienteDni}</div>
                      </td>
                      <td>{formatFecha(item.fecha)}</td>
                      <td>{item.estudiosResumen}</td>
                      <td>
                        {item.tienePdf ? (
                          <div className={styles.pdfActions}>
                            <button
                              className={styles.viewBtn}
                              onClick={() => handleVer(item)}
                              disabled={viewingId === item.id}
                            >
                              {viewingId === item.id ? 'Abriendo...' : '👁 Ver'}
                            </button>
                            <button
                              className={styles.downloadBtn}
                              onClick={() => handleDescargar(item)}
                              disabled={downloadingId === item.id}
                            >
                              {downloadingId === item.id ? 'Descargando...' : '⬇ Descargar'}
                            </button>
                          </div>
                        ) : (
                          <span className={styles.noPdf}>Sin PDF</span>
                        )}
                      </td>
                      {!esMedico && (
                        <td>
                          <div className={styles.accionesCell}>
                            <button
                              className={styles.editBtn}
                              onClick={() => navigate(`/analisis/${item.id}/editar`)}
                            >
                              <i className="bi bi-pencil-fill"></i> Editar
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => setConfirmItem(item)}
                              disabled={deletingId === item.id}
                            >
                              {deletingId === item.id ? 'Eliminando...' : (<><i className="bi bi-trash-fill"></i> Eliminar</>)}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </main>
    </div>

    <Modal
      isOpen={confirmItem !== null}
      onClose={() => setConfirmItem(null)}
      title="Eliminar análisis"
      width={420}
    >
      <p className={styles.confirmText}>
        ¿Estás seguro de que querés eliminar el análisis de{' '}
        <strong>{confirmItem?.pacienteApellido}, {confirmItem?.pacienteNombre}</strong>?
        Esta acción no se puede deshacer.
      </p>
      <div className={styles.confirmActions}>
        <button className={styles.cancelConfirmBtn} onClick={() => setConfirmItem(null)}>
          Cancelar
        </button>
        <button className={styles.deleteConfirmBtn} onClick={confirmarEliminar}>
          Eliminar
        </button>
      </div>
    </Modal>
    </>
  );
}
